import {
  applyActionCode,
  checkActionCode,
  confirmPasswordReset,
  parseActionCodeURL,
  verifyPasswordResetCode,
} from 'firebase/auth';
import { auth } from '../firebase';

export type ModoDeAccion = 'resetPassword' | 'verifyEmail' | 'recoverEmail' | 'desconocido';

export interface AccionDeCorreo {
  modo: ModoDeAccion;
  codigo: string;
  tenantId: string | null;
}

/**
 * Lee la acción que viene en la URL del correo.
 *
 * Se usa `parseActionCodeURL` y, si devuelve `null`, se cae a leer los
 * parámetros a mano. No es paranoia: el parser exige `apiKey` y un `mode`
 * conocido, y un enlace construido de otra forma (o un modo que el SDK no
 * mapea) dejaría la pantalla en blanco en vez de decir qué pasa.
 */
export function leerAccionDeLaURL(href: string): AccionDeCorreo | null {
  const parsed = parseActionCodeURL(href);
  if (parsed) {
    return {
      modo: normalizarModo(parsed.operation),
      codigo: parsed.code,
      tenantId: parsed.tenantId ?? null,
    };
  }

  const params = new URL(href).searchParams;
  const codigo = params.get('oobCode');
  if (!codigo) return null;
  return {
    modo: normalizarModo(params.get('mode')),
    codigo,
    tenantId: params.get('tenantId'),
  };
}

function normalizarModo(valor: string | null): ModoDeAccion {
  switch (valor) {
    case 'resetPassword':
    case 'PASSWORD_RESET':
      return 'resetPassword';
    case 'verifyEmail':
    case 'VERIFY_EMAIL':
      return 'verifyEmail';
    case 'recoverEmail':
    case 'RECOVER_EMAIL':
      return 'recoverEmail';
    default:
      return 'desconocido';
  }
}

/**
 * 🚨 Fija el silo ANTES de tocar el código.
 *
 * En Identity Platform multi-tenant el usuario vive dentro del tenant: sin
 * esto, `verifyPasswordResetCode` falla con `auth/invalid-action-code` y el
 * mensaje no dice nada sobre el tenant, así que parece un enlace caducado.
 * El enlace trae el `tenantId` en la query — medido el 2026-09-18 sobre un
 * `oobLink` real.
 */
export function fijarSilo(tenantId: string | null): void {
  if (tenantId) auth.tenantId = tenantId;
}

/** Valida el código de restablecimiento y devuelve a quién pertenece. */
export function correoDelCodigo(codigo: string): Promise<string> {
  return verifyPasswordResetCode(auth, codigo);
}

/** Aplica la contraseña nueva. Sirve igual para una invitación y para un olvido. */
export function definirContrasena(codigo: string, nueva: string): Promise<void> {
  return confirmPasswordReset(auth, codigo, nueva);
}

export function confirmarCorreo(codigo: string): Promise<void> {
  return applyActionCode(auth, codigo);
}

/** Revierte un cambio de correo que la persona no pidió. */
export async function revertirCambioDeCorreo(codigo: string): Promise<string> {
  const info = await checkActionCode(auth, codigo);
  await applyActionCode(auth, codigo);
  return info.data.email ?? '';
}

/**
 * El enlace caduca y se gasta al usarlo, así que estos dos casos son los
 * normales, no la excepción: merecen una salida ("pide otro"), no un error
 * genérico.
 */
export function mensajeDeErrorDeAccion(err: unknown): string {
  const codigo = (err as { code?: string })?.code ?? '';
  switch (codigo) {
    case 'auth/expired-action-code':
      return 'Este enlace ya venció. Pide uno nuevo desde «¿Olvidaste tu contraseña?».';
    case 'auth/invalid-action-code':
      return 'Este enlace ya se usó o no es válido. Pide uno nuevo desde «¿Olvidaste tu contraseña?».';
    case 'auth/user-disabled':
      return 'Esta cuenta está dada de baja. Habla con quien administra tu organización.';
    case 'auth/user-not-found':
      return 'La cuenta de este enlace ya no existe.';
    case 'auth/weak-password':
    case 'auth/password-does-not-meet-requirements':
      return 'La contraseña no cumple los requisitos mínimos.';
    default:
      return 'No se pudo completar la operación. Intenta de nuevo o pide un enlace nuevo.';
  }
}
